// Meilleures Ventes - Page publique
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  TrendingUp, ShoppingCart, Package, ChevronRight, Eye,
  Truck, Shield, Star, Award, Crown, Flame, Zap, Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

import MainNavigation from '../components/layout/MainNavigation';
import UnifiedHeader from '../components/layout/UnifiedHeader';
import { useBuyer } from '../contexts/BuyerContext';

// Carte Produit Best-seller
function BestsellerCard({ product, rank, onAddToCart }) {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-all relative"
    >
      {/* Badge Rang */}
      <div className={`absolute top-3 left-3 z-10 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-lg ${
        rank === 1 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
        rank === 2 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
        rank === 3 ? 'bg-gradient-to-br from-amber-600 to-amber-700' :
        'bg-gradient-to-br from-blue-500 to-blue-600'
      }`}>
        {rank <= 3 ? <Crown size={18} /> : `#${rank}`}
      </div>

      {/* Image produit */}
      <Link to={`/produit/${product.id}`} className="block">
        <div className="relative aspect-square bg-gray-50 cursor-pointer group overflow-hidden">
          {product.image ? (
            <img 
              src={product.image} 
              alt={product.title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <Package size={48} className="text-gray-300" />
            </div>
          )}
          {/* Overlay hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 bg-white/90 text-gray-900 px-4 py-2 rounded-full font-medium text-sm transition-opacity">
              Voir le produit
            </span>
          </div>
        </div>
      </Link>

      {/* Infos produit */}
      <div className="p-4">
        <Link to={`/produit/${product.id}`}>
          <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 min-h-[44px] hover:text-orange-600 transition-colors">
            {product.title}
          </h3>
        </Link>

        {/* Note et ventes */}
        <div className="flex items-center gap-3 mb-2">
          <div className="flex items-center gap-1">
            <Star size={14} className="text-yellow-500 fill-yellow-500" />
            <span className="text-sm font-medium text-gray-700">{product.rating}</span>
            <span className="text-xs text-gray-400">({product.reviewsCount})</span>
          </div>
          <div className="flex items-center gap-1 text-orange-600">
            <Flame size={14} />
            <span className="text-xs font-medium">{product.salesCount} vendus</span>
          </div>
        </div>

        {/* Prix */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-2xl font-bold text-gray-900">{product.price}€</span>
          {product.originalPrice && (
            <span className="text-base text-gray-400 line-through">{product.originalPrice}€</span>
          )}
        </div>

        {/* Livraison */}
        <div className="flex items-center gap-1.5 text-green-600 text-xs mb-4">
          <Truck size={14} />
          <span className="font-medium">Livraison gratuite</span>
        </div>

        {/* Boutons */}
        <div className="flex gap-2">
          <Button 
            onClick={() => navigate(`/produit/${product.id}`)}
            variant="outline"
            className="flex-1 border-gray-300 hover:border-orange-500 hover:text-orange-600 font-medium py-2.5 rounded-xl flex items-center justify-center gap-1.5"
          >
            <Eye size={16} />
            Voir
          </Button>
          <Button 
            onClick={() => onAddToCart(product)}
            className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-1.5"
          >
            <ShoppingCart size={16} />
            Ajouter
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default function MeilleuresVentesPage() {
  const navigate = useNavigate();
  const { addToCart } = useBuyer();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBestsellers();
  }, []);

  const fetchBestsellers = async () => {
    setLoading(true);
    try {
      // Données simulées - Top ventes
      setProducts([
        {
          id: 'demo-product-1',
          title: 'Perceuse-visseuse 18V Bosch Professional - Kit complet',
          price: 149,
          originalPrice: 189,
          image: 'https://images.unsplash.com/photo-1770763233593-74dfd0da7bf0?w=400',
          rating: 4.9,
          reviewsCount: 2847,
          salesCount: 15420,
        },
        {
          id: 'demo-product-3',
          title: 'Meuleuse angulaire Makita 125mm - 720W Pro',
          price: 79,
          originalPrice: 99,
          image: 'https://images.pexels.com/photos/13792037/pexels-photo-13792037.jpeg?auto=compress&w=400',
          rating: 4.8,
          reviewsCount: 1923,
          salesCount: 12350,
        },
        {
          id: 'demo-product-5',
          title: 'Niveau laser Bosch GLL 2-10 Professional',
          price: 129,
          image: 'https://images.pexels.com/photos/6474119/pexels-photo-6474119.jpeg?auto=compress&w=400',
          rating: 4.7,
          reviewsCount: 1456,
          salesCount: 9870,
        },
        {
          id: 'demo-product-4',
          title: 'Scie circulaire Bosch GKS 190 - 1400W',
          price: 159,
          originalPrice: 199,
          image: 'https://images.pexels.com/photos/30871012/pexels-photo-30871012.jpeg?auto=compress&w=400',
          rating: 4.8,
          reviewsCount: 1234,
          salesCount: 8540,
        },
        {
          id: 'demo-product-6',
          title: 'Tableau électrique 2 rangées Legrand pré-équipé',
          price: 89,
          image: 'https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg?auto=compress&w=400',
          rating: 4.6,
          reviewsCount: 987,
          salesCount: 7230,
        },
        {
          id: 'demo-product-2',
          title: 'Coffret embouts 32 pièces Bosch Professional',
          price: 39,
          originalPrice: 49,
          image: 'https://images.pexels.com/photos/5691656/pexels-photo-5691656.jpeg?auto=compress&w=400',
          rating: 4.7,
          reviewsCount: 2156,
          salesCount: 6890,
        },
        {
          id: 'demo-product-11',
          title: 'Mitigeur thermostatique douche Grohe',
          price: 189,
          originalPrice: 229,
          image: 'https://images.unsplash.com/photo-1761353855019-05f2f3ed9c43?w=400',
          rating: 4.5,
          reviewsCount: 876,
          salesCount: 5420,
        },
        {
          id: 'demo-product-16',
          title: 'Peinture acrylique blanc mat Tollens 10L',
          price: 59,
          image: 'https://images.unsplash.com/photo-1621685682093-3b8016dcb57d?w=400',
          rating: 4.6,
          reviewsCount: 1543,
          salesCount: 4980,
        },
      ]);
    } catch (e) {
      console.error('Error fetching bestsellers:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart({
      id: product.id,
      name: product.title,
      price: product.price,
      image: product.image,
      quantity: 1,
    });
    toast.success(`${product.title.substring(0, 30)}... ajouté au panier !`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="meilleures-ventes-page">
      {/* Header Global */}
      <UnifiedHeader />
      
      {/* Navigation Marketplace */}
      <MainNavigation />

      {/* Spacer pour compenser les headers fixed */}
      <div className="h-11 md:hidden" />
      <div className="hidden md:block" style={{ height: '148px' }} />

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-10 relative">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <TrendingUp size={32} className="text-white" />
              <h1 className="text-3xl md:text-4xl font-black text-white">MEILLEURES VENTES</h1>
              <Award size={32} className="text-yellow-300" />
            </div>
            <p className="text-white/90 text-base mb-4">
              Les produits préférés des professionnels du bâtiment
            </p>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur rounded-full px-5 py-2.5">
              <Flame size={18} className="text-yellow-300" />
              <span className="text-white font-medium text-sm">Top {products.length} des ventes ce mois</span>
            </div>
          </div>
        </div>
      </div>

      {/* Badges de confiance */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-6 md:gap-8 flex-wrap">
            {[
              { icon: Zap, text: 'Sans intermédiaire' },
              { icon: Tag, text: 'Zéro commission' },
              { icon: Shield, text: 'Paiement sécurisé' },
              { icon: Award, text: 'Vendeur certifié' },
              { icon: Star, text: 'Avis vérifiés' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-600">
                <item.icon size={18} className="text-orange-500" />
                <span className="text-xs md:text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <main className="flex-1 max-w-7xl mx-auto px-4 py-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              Top {products.length} des ventes
            </h2>
            <p className="text-gray-500 text-sm">Classement basé sur les ventes des 30 derniers jours</p>
          </div>
          <Link 
            to="/produits"
            className="text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1 text-sm"
          >
            Voir tous les produits <ChevronRight size={16} />
          </Link>
        </div>

        {/* Grille des produits - 4 par ligne */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {products.map((product, index) => (
              <BestsellerCard
                key={product.id}
                product={product}
                rank={index + 1}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 md:p-8 text-center">
          <TrendingUp size={28} className="text-amber-400 mx-auto mb-3" />
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
            Tu es un pro du bâtiment ?
          </h3>
          <p className="text-gray-400 mb-5 text-sm">
            Rejoins les milliers de vendeurs qui font confiance à AbracadaWorld
          </p>
          <Link to="/offre-fondateur">
            <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white font-bold px-6 py-3 rounded-xl">
              Devenir vendeur
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer Global */}
      
    </div>
  );
}
